import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trash2, ShoppingBag, LogOut, RefreshCw, Trophy, Copy, Check } from "lucide-react";
import { useCustomer } from "../context/CustomerContext";
import { useCart } from "../context/CartContext";
import { customerApi, api } from "../lib/api";
import Nav from "../components/Nav";
import Footer from "../components/Footer";

const layerHasData = (l) => !!(l && (l.dyes?.length || l.micas?.length || l.pearls?.length || l.glitters?.length));
const layerLine = (l) =>
  [
    l?.dyes?.length ? l.dyes.join(", ") : "",
    l?.micas?.length ? l.micas.join(", ") : "",
    l?.pearls?.length ? `Pearl: ${l.pearls.join(", ")}` : "",
    l?.glitters?.length ? `Glitter: ${l.glitters.join(", ")}` : "",
  ]
    .filter(Boolean)
    .join(" · ");

export default function Account() {
  const navigate = useNavigate();
  const { customer, loading, exchangeGoogle, logout } = useCustomer();
  const { addItem } = useCart();
  const [tab, setTab] = useState("recipes");
  const [recipes, setRecipes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [amb, setAmb] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [monthlyLeader, setMonthlyLeader] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [ready, setReady] = useState(false);
  const processedCallback = useRef(false);

  // Handle Google OAuth callback (session_id in URL fragment)
  useEffect(() => {
    const hash = window.location.hash || "";
    if (hash.includes("session_id=") && !processedCallback.current) {
      processedCallback.current = true;
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      (async () => {
        try {
          await exchangeGoogle(sid);
          window.history.replaceState(null, "", "/account");
          toast.success("Signed in with Google");
        } catch {
          toast.error("Google sign-in failed");
          navigate("/login");
        }
      })();
    }
  }, [exchangeGoogle, navigate]);

  // Redirect to login if not authenticated (and not mid-callback)
  useEffect(() => {
    if (loading) return;
    if (!customer && !window.location.hash.includes("session_id=")) {
      navigate("/login");
    }
  }, [customer, loading, navigate]);

  const loadData = useCallback(async () => {
    try {
      const [r, o, a] = await Promise.all([
        customerApi.get("/customer/recipes"),
        customerApi.get("/customer/orders"),
        customerApi.get("/ambassador/me"),
      ]);
      setRecipes(r.data);
      setOrders(o.data);
      const isAmb = a.data && a.data.is_ambassador;
      setAmb(isAmb ? a.data : null);
      if (isAmb) {
        try {
          const [lb, ml] = await Promise.all([
            customerApi.get("/ambassador/leaderboard"),
            customerApi.get("/ambassador/monthly-leader"),
          ]);
          setLeaderboard(lb.data);
          setMonthlyLeader(ml.data?.leader || null);
        } catch { /* non-blocking */ }
      }
      setReady(true);
    } catch {
      /* handled by auth redirect */
    }
  }, []);

  const redeemPack = async () => {
    if (redeeming) return;
    setRedeeming(true);    try {
      await customerApi.post("/ambassador/redeem");
      toast.success("Redemption requested — we'll ship your free pack soon");
      const { data } = await customerApi.get("/ambassador/me");
      setAmb(data && data.is_ambassador ? data : null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not redeem");
    } finally {
      setRedeeming(false);
    }
  };

  useEffect(() => {
    if (customer) loadData();
  }, [customer, loadData]);

  const deleteRecipe = async (id) => {
    try {
      await customerApi.delete(`/customer/recipes/${id}`);
      setRecipes((prev) => prev.filter((x) => x.id !== id));
      toast.success("Recipe removed");
    } catch {
      toast.error("Could not delete");
    }
  };

  const copyLink = async () => {
    if (!amb) return;
    const link = `${window.location.origin}/?ref=${amb.code}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy — long-press to copy manually");
    }
  };


  const reorder = async (recipe) => {
    try {
      const { data: products } = await api.get("/products");
      const custom = products.find((p) => p.custom);
      if (!custom) {
        toast.error("Custom pour product is unavailable");
        return;
      }
      const layerSpecs = (l, prefix = "") => {
        const o = {};
        if (l?.dyes?.length) o[`${prefix}Dyes`] = l.dyes;
        if (l?.micas?.length) o[`${prefix}Mica`] = l.micas;
        if (l?.pearls?.length) o[`${prefix}Pearl`] = l.pearls;
        if (l?.glitters?.length) o[`${prefix}Glitter`] = l.glitters;
        return o;
      };
      const layerHas = (l) => l && (l.dyes?.length || l.micas?.length || l.pearls?.length || l.glitters?.length);
      let specs = { ...(recipe.product_type ? { "Product Type": recipe.product_type } : {}) };
      const nameSpec = recipe.name ? { "Recipe Name": recipe.name } : {};
      if (recipe.split) {
        specs = { ...specs, Recipe: "Build your own — Split color", ...nameSpec, ...layerSpecs(recipe.top, "Top — "), ...layerSpecs(recipe.bottom, "Bottom — ") };
      } else if (layerHas(recipe.base)) {
        specs = { ...specs, Recipe: "Build your own", ...nameSpec, ...layerSpecs(recipe.base) };
      } else {
        // legacy single-value recipes
        specs = {
          ...specs,
          Recipe: "Build your own",
          ...nameSpec,
          ...(recipe.base_pigment ? { "Base Pigment": recipe.base_pigment } : {}),
          ...(recipe.mica ? { "Mica Powder": recipe.mica } : {}),
          ...(recipe.pearl ? { Pearl: recipe.pearl } : {}),
          ...(recipe.glitter ? { Glitter: recipe.glitter } : {}),
          ...(recipe.sheen ? { Sheen: recipe.sheen } : {}),
        };
      }
      if (recipe.instructions) specs.Instructions = recipe.instructions;
      addItem(custom, null, 1, specs);
      toast.success(`${recipe.name || "Recipe"} added to cart`);
    } catch {
      toast.error("Could not add to cart");
    }
  };

  if (loading || (!customer && window.location.hash.includes("session_id="))) {
    return (
      <div className="bg-[#04070A] min-h-screen flex items-center justify-center">
        <p className="font-mono-plex text-xs tracking-widest uppercase text-slate-500">Loading…</p>
      </div>
    );
  }
  if (!customer) return <div className="bg-[#04070A] min-h-screen" />;

  const shareLink = amb ? `${window.location.origin}/?ref=${amb.code}` : "";

  return (
    <div className="bg-[#04070A] min-h-screen">
      <Nav />
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 pt-32 pb-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
          className="flex items-end justify-between flex-wrap gap-4"
        >
          <div>
            <span className="font-mono-plex text-[10px] tracking-[0.25em] uppercase text-orange-500">Angler Account</span>
            <h1 className="font-display text-5xl uppercase tracking-tighter mt-2 leading-none" data-testid="account-name">
              {customer.name || "Welcome"}
            </h1>
            <p className="font-mono-plex text-xs text-slate-500 mt-2">{customer.email}</p>
          </div>
          <button
            onClick={() => { logout(); navigate("/"); }}
            className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono-plex text-[10px] tracking-widest uppercase text-slate-300 hover:bg-white/5 transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-3.5 h-3.5" /> Log Out
          </button>
        </motion.div>

        <div className="flex gap-3 mt-10 border-b border-white/10">
          {[
            { k: "recipes", l: "Saved Recipes" },
            { k: "orders", l: "Order History" },
            ...(amb ? [{ k: "ambassador", l: "Ambassador" }] : []),
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`font-mono-plex text-xs tracking-[0.15em] uppercase px-4 py-3 -mb-px border-b-2 transition-colors ${
                tab === t.k ? "border-orange-500 text-white" : "border-transparent text-slate-500 hover:text-slate-300"
              }`}
              data-testid={`tab-${t.k}`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "recipes" && (
          <div className="mt-8" data-testid="recipes-panel">
            {!ready ? (
              <p className="text-slate-600 text-sm">Loading recipes…</p>
            ) : recipes.length === 0 ? (
              <div className="border border-dashed border-white/15 p-10 text-center">
                <p className="text-slate-400 text-sm">No saved recipes yet.</p>
                <button onClick={() => navigate("/shop")} className="mt-4 font-mono-plex text-xs tracking-widest uppercase text-orange-400 hover:text-orange-300">Build one in the Custom Pour Studio →</button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-5">
                {recipes.map((r) => (
                  <div key={r.id} className="border border-white/10 bg-[#0A1118] p-5" data-testid={`recipe-${r.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-2xl uppercase tracking-tight leading-none">{r.name || "Untitled Recipe"}</h3>
                      <button onClick={() => deleteRecipe(r.id)} className="text-slate-600 hover:text-red-400 transition-colors shrink-0" data-testid={`recipe-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <dl className="mt-4 space-y-1.5 font-mono-plex text-xs text-slate-400">
                      {r.product_type && <div><span className="text-slate-600">Type: </span>{r.product_type}</div>}
                      {r.split ? (
                        <>
                          <div><span className="text-slate-600">Top: </span>{layerLine(r.top) || "—"}</div>
                          <div><span className="text-slate-600">Bottom: </span>{layerLine(r.bottom) || "—"}</div>
                        </>
                      ) : layerHasData(r.base) ? (
                        <>
                          {r.base.dyes?.length ? <div><span className="text-slate-600">Dyes: </span>{r.base.dyes.join(", ")}</div> : null}
                          {r.base.micas?.length ? <div><span className="text-slate-600">Mica: </span>{r.base.micas.join(", ")}</div> : null}
                          {r.base.pearls?.length ? <div><span className="text-slate-600">Pearl: </span>{r.base.pearls.join(", ")}</div> : null}
                          {r.base.glitters?.length ? <div><span className="text-slate-600">Glitter: </span>{r.base.glitters.join(", ")}</div> : null}
                        </>
                      ) : (
                        <>
                          {r.base_pigment && <div><span className="text-slate-600">Base Pigment: </span>{r.base_pigment}</div>}
                          {r.mica && <div><span className="text-slate-600">Mica: </span>{r.mica}</div>}
                          {r.pearl && <div><span className="text-slate-600">Pearl: </span>{r.pearl}</div>}
                          {r.glitter && <div><span className="text-slate-600">Glitter: </span>{r.glitter}</div>}
                        </>
                      )}
                      {r.instructions && <div className="text-slate-500 italic">“{r.instructions}”</div>}
                    </dl>
                    <button
                      onClick={() => reorder(r)}
                      className="mt-5 w-full bg-orange-500 text-[#04070A] font-mono-plex text-[10px] tracking-[0.2em] uppercase py-3 flex items-center justify-center gap-2 hover:bg-orange-600 transition-colors"
                      data-testid={`recipe-reorder-${r.id}`}
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Reorder
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-8" data-testid="orders-panel">
            {!ready ? (
              <p className="text-slate-600 text-sm">Loading orders…</p>
            ) : orders.length === 0 ? (
              <div className="border border-dashed border-white/15 p-10 text-center">
                <p className="text-slate-400 text-sm">No orders yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} className="border border-white/10 bg-[#0A1118] p-5" data-testid={`order-${o.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <span className="font-mono-plex text-xs text-slate-500">#{o.id.slice(0, 8)} · {new Date(o.created_at).toLocaleDateString()}</span>
                      <div className="flex items-center gap-3">
                        <span className="font-mono-plex text-[10px] tracking-widest uppercase px-2.5 py-1 border border-white/15 text-slate-300">{o.status}</span>
                        <span className="font-display text-xl text-orange-500">${o.total?.toFixed(2)}</span>
                      </div>
                    </div>
                    <ul className="mt-3 space-y-1 text-sm text-slate-300">
                      {o.items?.map((it, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <ShoppingBag className="w-3 h-3 text-slate-600" />
                          {it.quantity}× {it.name}{it.color ? ` — ${it.color}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "ambassador" && amb && (
          <div className="mt-8" data-testid="ambassador-panel">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="border border-white/10 bg-[#0A1118] p-5">
                <span className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500">Your Code</span>
                <p className="font-display text-3xl uppercase tracking-tight text-orange-500 mt-2" data-testid="amb-code">{amb.code}</p>
                <p className="text-slate-500 text-xs mt-1">Share it — friends get {amb.discount_pct}% off.</p>
              </div>
              <div className="border border-white/10 bg-[#0A1118] p-5">
                <span className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500">Points Available</span>
                <p className="font-display text-3xl text-white mt-2" data-testid="amb-points">{amb.points_available}</p>
                <p className="text-slate-500 text-xs mt-1">{amb.points_earned} earned · {amb.points_redeemed} redeemed</p>
              </div>
              <div className="border border-white/10 bg-[#0A1118] p-5">
                <span className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500">Paid Referrals</span>
                <p className="font-display text-3xl text-white mt-2">{amb.referrals}</p>
                <p className="text-slate-500 text-xs mt-1">1 point per paid order using your code.</p>
              </div>
            </div>

            <div className="border border-white/10 bg-[#0A1118] p-5 mt-5" data-testid="share-link-card">
              <span className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500">Your Shareable Link</span>
              <p className="text-slate-400 text-xs mt-1">Send this link — the {amb.discount_pct}% off applies automatically at checkout.</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <code className="flex-1 min-w-[220px] bg-black/40 border border-white/10 px-3 py-2.5 text-sm text-slate-200 truncate" data-testid="share-link">{shareLink}</code>
                <button onClick={copyLink} className="flex items-center gap-2 border border-white/20 px-4 py-2.5 font-mono-plex text-[10px] tracking-widest uppercase hover:bg-white/5 transition-colors" data-testid="copy-link-btn">
                  {copied ? <><Check className="w-3.5 h-3.5 text-[#AEB358]" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                </button>
              </div>
            </div>

            <div className="border border-[#AEB358]/30 bg-[#AEB358]/5 p-6 mt-5">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="font-display text-2xl uppercase tracking-tight">Free Pack Rewards</h3>
                  <p className="text-slate-400 text-sm mt-1">
                    {amb.threshold} points = 1 free pack. You have <span className="text-[#AEB358]">{amb.packs_available}</span> pack{amb.packs_available === 1 ? "" : "s"} ready to claim.
                  </p>
                </div>
                <button
                  onClick={redeemPack}
                  disabled={redeeming || amb.points_available < amb.threshold}
                  className="bg-orange-500 text-[#04070A] font-mono-plex text-[10px] tracking-[0.2em] uppercase px-6 py-3.5 hover:bg-orange-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="redeem-pack-button"
                >
                  {redeeming ? "Requesting…" : "Request Free Pack"}
                </button>
              </div>
              {amb.points_available < amb.threshold && (
                <p className="text-slate-500 text-xs mt-3">{amb.threshold - amb.points_available} more point{amb.threshold - amb.points_available === 1 ? "" : "s"} to your next free pack.</p>
              )}
            </div>

            {leaderboard.length > 0 && (
              <div className="mt-6" data-testid="leaderboard">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-[#AEB358]" />
                  <span className="font-mono-plex text-[10px] tracking-[0.2em] uppercase text-slate-500">Top Ambassadors</span>
                </div>
                {monthlyLeader && (
                  <p className="mt-2 text-sm text-slate-300" data-testid="monthly-leader">
                    <span className="text-[#AEB358]">🏆 Leader this month:</span> {monthlyLeader.name} · {monthlyLeader.points} referral{monthlyLeader.points === 1 ? "" : "s"}
                  </p>
                )}
                <div className="mt-3 border border-white/10 bg-[#0A1118] divide-y divide-white/5">
                  {leaderboard.map((row) => (
                    <div
                      key={row.rank}
                      className={`flex items-center justify-between px-4 py-3 ${row.is_you ? "bg-orange-500/10" : ""}`}
                      data-testid={`leaderboard-row-${row.rank}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`font-display text-lg w-8 ${row.rank <= 3 ? "text-[#AEB358]" : "text-slate-500"}`}>#{row.rank}</span>
                        <span className={`text-sm ${row.is_you ? "text-orange-400 font-semibold" : "text-slate-300"}`}>
                          {row.name}{row.is_you ? " (You)" : ""}
                        </span>
                      </div>
                      <span className="font-mono-plex text-xs text-slate-400">{row.points} pts</span>
                    </div>
                  ))}
                </div>
              </div>
            )}


            {amb.redemptions?.length > 0 && (
              <div className="mt-6">
                <span className="font-mono-plex text-[10px] tracking-widest uppercase text-slate-500">Reward History</span>
                <div className="mt-3 space-y-2">
                  {amb.redemptions.map((r) => (
                    <div key={r.id} className="flex items-center justify-between border border-white/10 bg-[#0A1118] px-4 py-3" data-testid={`redemption-${r.id}`}>
                      <span className="text-sm text-slate-300">{r.packs} free pack · {r.points_spent} pts · {new Date(r.created_at).toLocaleDateString()}</span>
                      <span className={`font-mono-plex text-[10px] tracking-widest uppercase px-2.5 py-1 border ${r.status === "fulfilled" ? "border-[#AEB358]/40 text-[#AEB358]" : "border-orange-500/40 text-orange-400"}`}>{r.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
